import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { BRAND, whatsappLink } from "@/lib/brand";
import copy from "@/data/content/contact.json";

export default function Contact() {
  const [form, setForm] = useState({ name: "", dates: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and a short message.");
      return;
    }
    const msg = `Hi IndiAbode! I'm ${form.name}.${
      form.dates ? ` Dates: ${form.dates}.` : ""
    } ${form.message}`;
    window.open(whatsappLink(msg), "_blank");
    toast.success("Opening WhatsApp — we'll reply shortly!");
  };

  const channels = [
    { icon: Phone, label: "Call us", value: BRAND.phone, href: `tel:${BRAND.phoneRaw}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with us", href: whatsappLink(), testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email" },
  ];

  return (
    <main data-testid="contact-page" className="pt-[60px] md:pt-[72px] bg-ivory">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-14 md:pb-20">
        <div className="max-w-2xl">
          <Reveal>
            <span className="text-xs tracking-[0.25em] uppercase text-terracotta font-medium">{copy.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 font-serif text-4xl md:text-6xl font-semibold text-brown leading-[1.1]">
              {copy.heading}
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 text-brown/75 text-lg leading-relaxed">
              {copy.intro}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1.1fr]">
          {/* Channels + map */}
          <div className="space-y-4">
            {channels.map((c) => (
              <Reveal key={c.label}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-[0_8px_30px_rgba(74,49,37,0.06)] hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <span className="h-12 w-12 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
                    <c.icon className="h-5 w-5 text-terracotta" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-brown/50">{c.label}</span>
                    <span className="block text-brown font-medium">{c.value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
            <Reveal>
              <div className="rounded-2xl overflow-hidden aspect-video shadow-[0_8px_30px_rgba(74,49,37,0.08)]">
                <iframe
                  title="IndiAbode location in Varanasi"
                  src={BRAND.mapsEmbed}
                  loading="lazy"
                  className="h-full w-full border-0"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-brown/60">
                <MapPin className="h-4 w-4 text-gold" /> {BRAND.city}
              </p>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-3xl bg-white p-7 md:p-10 shadow-[0_12px_40px_rgba(74,49,37,0.08)]"
            >
              <h2 className="font-serif text-2xl font-semibold text-brown">{copy.formHeading}</h2>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="text-sm font-medium text-brown/70">Your name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-input-name"
                    placeholder="e.g. Ananya"
                    className="mt-2 w-full rounded-xl border border-brown/15 bg-ivory px-4 py-3 text-brown placeholder:text-brown/40 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-brown/70">Travel dates (optional)</label>
                  <input
                    type="text"
                    value={form.dates}
                    onChange={update("dates")}
                    data-testid="contact-input-dates"
                    placeholder="e.g. 12–15 March"
                    className="mt-2 w-full rounded-xl border border-brown/15 bg-ivory px-4 py-3 text-brown placeholder:text-brown/40 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-brown/70">Message</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    placeholder="Tell us who's travelling and what kind of stay you're after."
                    className="mt-2 w-full rounded-xl border border-brown/15 bg-ivory px-4 py-3 text-brown placeholder:text-brown/40 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none transition resize-none"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-base font-medium text-white hover:bg-terracotta-dark transition-colors duration-300"
                >
                  <Send className="h-4 w-4" /> Send via WhatsApp
                </button>
                <p className="text-center text-xs text-brown/50">
                  {copy.formNote}
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
